import { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { GetPurchaseOrderResponse } from "../../../../api/services/client/models";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiViewMorePopOver,
} from "../../../../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  PAGE_IDS,
  viewMorePopOverGridSize,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getPOStatusFromExpirationDate,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../redux/slices/appState/appStateSelector";
import { AddPurchaseOrderFormKey as ViewPurchaseOrderFormKey } from "../../modelsAndSchemas/Models";
import { purchaseOrdersListSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import PurchaseOrderTransactions from "../manageTransaction/transactionsTable/PurchaseOrderTransactions";
import { ViewPurchaseOrderWrapper } from "./ViewPurchaseOrder.style";

export interface ViewPurchaseOrderProps {
  open: boolean;
  purchaseOrderData?: GetPurchaseOrderResponse;
  showTransactions?: boolean;
  onClose: () => void;
  onPurchaseOrderEdit?: (purchaseOrderId: string) => void;
  isEditable?: boolean;
}

export const ViewPurchaseOrder: FC<ViewPurchaseOrderProps> = ({
  open,
  purchaseOrderData,
  showTransactions = false,
  onClose,
  onPurchaseOrderEdit,
  isEditable = true,
}) => {
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { isMobile } = useMediaQueries();
  const currencyList = useSelector(currencyListSelector);

  const [currencyDetails, setCurrencyDetails] = useState<any>();

  useEffect(() => {
    const currencyData = getCurrencyNameAndLocaleById(
      currencyList,
      purchaseOrderData?.currencyTypeId,
    );
    if (currencyData) {
      setCurrencyDetails(currencyData);
    }
  }, [currencyList, purchaseOrderData?.currencyTypeId]);

  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();

  const pageId = PAGE_IDS.VIEW_PURCHASE_ORDER;
  const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

  const {
    purchaseOrderTitle,
    viewPurchaseOrderHeading,
    poNumber,
    poEffectiveDate,
    poExpirationDate,
    poStatus,
    poAmount,
    statusList,
    reOpenDate,
    reOpenReasonDropdown,
    poReOpenReasonList,
  } = purchaseOrdersListSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(purchaseOrderTitle, key);
  };

  const handleEditPurchaseOrder = () => {
    onPurchaseOrderEdit?.(purchaseOrderData?.purchaseOrderId ?? "");
  };

  const modalTitle = `${replaceHashWithValue(getLabel(viewPurchaseOrderHeading), purchaseOrderData?.poNumber ?? "")}`;

  const isPOActive = useMemo(() => {
    return purchaseOrderData?.statusId === STATUS_BADGE_TYPE.active;
  }, [purchaseOrderData?.statusId]);

  const formatCurrency = (): string => {
    const formattedAmount = `${currencyDetails?.currencyName} ${getFormattedAmount(Number(purchaseOrderData?.currentAmount), currencyDetails?.currencyLocale)}`;
    return formattedAmount;
  };

  const getFormattedExpirationDate = () => {
    return convertToUserFormat(
      purchaseOrderData?.expiryDate,
      userCountryDateFormat,
    );
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      hasLargeContent
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      tertiaryButtonTitle={
        isPOActive && isEditable ? commonLabels.edit : undefined
      }
      onSecondaryButtonClick={onClose}
      onTertiaryButtonClick={handleEditPurchaseOrder}
      stackButtonsOnMobile={true}
    >
      <ViewPurchaseOrderWrapper>
        <MuiBox className={showTransactions ? "content" : " separator content"}>
          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 5}>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                id={`${pageId}-${ViewPurchaseOrderFormKey.poNumber}`}
                label={getLabel(poNumber)}
                value={purchaseOrderData?.poNumber}
              />
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                id={`${pageId}-${ViewPurchaseOrderFormKey.effectiveDate}`}
                label={getLabel(poEffectiveDate)}
                value={convertToUserFormat(
                  purchaseOrderData?.effectiveDate,
                  userCountryDateFormat,
                )}
              />
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                id={`${pageId}-${ViewPurchaseOrderFormKey.initialAmount}`}
                label={getLabel(poAmount)}
                value={formatCurrency()}
              />
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                id={`${pageId}-${ViewPurchaseOrderFormKey.expiryDate}`}
                label={getLabel(poExpirationDate)}
                value={
                  purchaseOrderData?.expiryDate &&
                  isNotEmpty(purchaseOrderData?.expiryDate)
                    ? getFormattedExpirationDate()
                    : EMPTY_VALUE_DASH
                }
              />
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                id={`${pageId}-${ViewPurchaseOrderFormKey.statusId}`}
                label={getLabel(poStatus)}
                value={
                  <StatusBadge
                    status={getPOStatusFromExpirationDate(
                      purchaseOrderData?.statusId,
                      getFormattedExpirationDate(),
                    )}
                    label={fetchValueByKeyOfDropdown(
                      purchaseOrderTitle,
                      statusList,
                      getPOStatusFromExpirationDate(
                        purchaseOrderData?.statusId,
                        getFormattedExpirationDate(),
                      ),
                    )}
                  />
                }
              />
            </MuiGrid>
          </MuiGrid>
          {purchaseOrderData?.reOpenReason && (
            <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 5}>
              <MuiGrid size={gridSize} className="po-reopen">
                <MuiDoubleLabel
                  label={getLabel(reOpenDate)}
                  value={convertToUserFormat(
                    purchaseOrderData?.reOpenDate,
                    userCountryDateFormat,
                  )}
                  replaceEmptyWithDash
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className="po-reopen">
                <MuiDoubleLabel
                  label={getLabel(reOpenReasonDropdown)}
                  value={fetchValueByKeyOfDropdown(
                    purchaseOrderTitle,
                    poReOpenReasonList,
                    purchaseOrderData?.reOpenReason,
                  )}
                  replaceEmptyWithDash
                  isValueWithEllipsis
                />
              </MuiGrid>
            </MuiGrid>
          )}
          <MuiGrid container>
            <MuiGrid
              container
              className="notes-container"
              size={viewMorePopOverGridSize}
            >
              <MuiDoubleLabel
                id={`${pageId}-${ViewPurchaseOrderFormKey.notes}`}
                label={commonLabels.notes}
                value={
                  purchaseOrderData?.notes &&
                  isNotEmpty(purchaseOrderData?.notes) ? (
                    <MuiViewMorePopOver
                      anchorText={commonLabels.viewMore}
                      message={purchaseOrderData?.notes}
                      placement="bottom-end"
                    />
                  ) : (
                    EMPTY_VALUE_DASH
                  )
                }
              />
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        {showTransactions && (
          <PurchaseOrderTransactions
            currencyTypeId={purchaseOrderData?.currencyTypeId ?? ""}
            purchaseOrderId={purchaseOrderData?.purchaseOrderId ?? ""}
          />
        )}
        <MuiBox />
      </ViewPurchaseOrderWrapper>
    </MuiModal>
  );
};
